import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface PsychStage {
  label: string;
  description: string;
  icon: string;
}

interface PsychExample {
  scenario: string;
  icon: string;
}

interface PsychologyProps {
  conceptName: string;
  conceptNameEn: string;
  definition: string;
  stages: PsychStage[];
  examples: PsychExample[];
  takeaway: string;
  selfReflection: string;
  themeColors?: { primary: string; secondary: string };
}

const DEFAULT_PROPS: PsychologyProps = {
  conceptName: '达克效应',
  conceptNameEn: 'Dunning-Kruger Effect',
  definition: '能力欠缺的人往往高估自己的能力，而真正有能力的人反而倾向于低估自己',
  stages: [
    { label: '愚昧之巅', description: '初学者的盲目自信', icon: '⛰️' },
    { label: '绝望之谷', description: '发现自己的无知', icon: '🕳️' },
    { label: '开悟之坡', description: '持续学习，稳步上升', icon: '📈' },
    { label: '持续平稳', description: '真正的专家谦逊', icon: '🏔️' },
  ],
  examples: [
    { scenario: '刚学编程一周就觉得自己能开发 App', icon: '💻' },
    { scenario: '读了一篇科普就认为自己比专家更懂', icon: '📰' },
    { scenario: '新手投资者连续盈利后觉得自己是股神', icon: '📊' },
  ],
  takeaway: '真正的智慧，始于承认自己的无知',
  selfReflection: '你目前在哪个阶段？',
  themeColors: { primary: '#14b8a6', secondary: '#a78bfa' },
};

const DKCurve: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const progress = interpolate(frame, [160, 340], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const curveY = (t: number) => {
    const confidence =
      0.1 +
      0.85 * Math.exp(-((t - 0.15) * (t - 0.15)) / 0.008) -
      0.08 * Math.exp(-((t - 0.45) * (t - 0.45)) / 0.02) +
      0.55 * (1 / (1 + Math.exp(-12 * (t - 0.7))));
    const clamped = Math.max(0, Math.min(1, confidence));
    return 260 - clamped * 230;
  };

  const points = [];
  const totalPoints = 100;
  for (let i = 0; i <= totalPoints * progress; i++) {
    const t = i / totalPoints;
    const x = t * 900 + 50;
    points.push(`${x},${curveY(t)}`);
  }

  return (
    <svg width="960" height="340" viewBox="0 0 960 340">
      {/* Axis lines */}
      <line x1="50" y1="280" x2="950" y2="280" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <line x1="50" y1="20" x2="50" y2="280" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />

      {/* Axis labels */}
      <text x="500" y="315" fill="rgba(255,255,255,0.3)" fontSize="30" textAnchor="middle">
        经验 →
      </text>
      <text
        x="20"
        y="150"
        fill="rgba(255,255,255,0.3)"
        fontSize="30"
        textAnchor="middle"
        transform="rotate(-90, 20, 150)"
      >
        自信程度 →
      </text>

      {/* The curve */}
      {points.length > 1 && (
        <polyline
          points={points.join(' ')}
          fill="none"
          stroke="url(#curveGradient)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      <defs>
        <linearGradient id="curveGradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#14b8a6" />
          <stop offset="50%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#f472b6" />
        </linearGradient>
      </defs>

      {/* Stage markers */}
      {progress > 0.15 && (
        <circle cx={50 + 0.15 * 900} cy={curveY(0.15)} r="6" fill="#f472b6" opacity={Math.min(1, (progress - 0.15) * 10)} />
      )}
      {progress > 0.45 && (
        <circle cx={50 + 0.45 * 900} cy={curveY(0.45)} r="6" fill="#818cf8" opacity={Math.min(1, (progress - 0.45) * 10)} />
      )}
      {progress > 0.75 && (
        <circle cx={50 + 0.75 * 900} cy={curveY(0.75)} r="6" fill="#34d399" opacity={Math.min(1, (progress - 0.75) * 10)} />
      )}
    </svg>
  );
};

export const PsychologyTemplate: React.FC<Partial<PsychologyProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const { primary, secondary } = p.themeColors!;

  // Phase 1: Concept intro (0-80)
  const titleProgress = spring({ frame, fps, config: { damping: 16, stiffness: 100 } });
  const defOpacity = interpolate(frame, [40, 70], [0, 1], { extrapolateRight: 'clamp' });
  const defY = spring({ frame: frame - 40, fps, from: 25, to: 0, config: { damping: 20, stiffness: 100 } });

  // Phase 2: Curve (80-340) — animated in DKCurve component

  // Phase 3: Stages (340-460)
  const stageStart = 340;
  const stageInterval = 30;

  // Phase 4: Examples (460-560)
  const exampleStart = 460;
  const exampleInterval = 30;

  // Phase 5: Takeaway + reflection (560-640)
  const takeawayOpacity = interpolate(frame, [560, 590], [0, 1], { extrapolateRight: 'clamp' });
  const reflectionOpacity = interpolate(frame, [600, 630], [0, 1], { extrapolateRight: 'clamp' });

  // Subtle orb animation
  const orbPulse = interpolate(Math.sin(frame / 20), [-1, 1], [0.85, 1.15]);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(170deg, #0f1729 0%, #1a1040 40%, #110d2a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Helvetica Neue", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Decorative orbs */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          right: 60,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${primary}18, transparent 70%)`,
          transform: `scale(${orbPulse})`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 600,
          left: 40,
          width: 180,
          height: 180,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${secondary}15, transparent 70%)`,
          transform: `scale(${1.3 - (orbPulse - 0.85)})`,
        }}
      />

      {/* Header: Concept name */}
      <div
        style={{
          position: 'absolute',
          top: 70,
          left: 0,
          right: 0,
          textAlign: 'center',
          transform: `scale(${titleProgress})`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '8px 28px',
            background: `${primary}18`,
            border: `1px solid ${primary}40`,
            borderRadius: 50,
            fontSize: 36,
            color: primary,
            fontWeight: 600,
            letterSpacing: '0.1em',
            marginBottom: 16,
          }}
        >
          心理学概念
        </div>
        <h1
          style={{
            fontSize: 68,
            fontWeight: 800,
            margin: '0 60px',
            lineHeight: 1.2,
            background: `linear-gradient(135deg, ${primary}, ${secondary})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {p.conceptName}
        </h1>
        <p style={{ fontSize: 36, color: 'rgba(255,255,255,0.4)', marginTop: 8, fontStyle: 'italic' }}>
          {p.conceptNameEn}
        </p>
      </div>

      {/* Definition */}
      <div
        style={{
          position: 'absolute',
          top: 330,
          left: 60,
          right: 60,
          opacity: defOpacity,
          transform: `translateY(${defY}px)`,
        }}
      >
        <div
          style={{
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 20,
            padding: '24px 32px',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <span style={{ fontSize: 32, flexShrink: 0, marginTop: 4 }}>🧠</span>
            <span style={{ fontSize: 34, lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
              {p.definition}
            </span>
          </div>
        </div>
      </div>

      {/* DK Curve visualization */}
      <div
        style={{
          position: 'absolute',
          top: 540,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 38, color: 'rgba(255,255,255,0.35)', marginBottom: 12 }}>能力-自信曲线</div>
        <DKCurve frame={frame} fps={fps} />
      </div>

      {/* Stages */}
      <div
        style={{
          position: 'absolute',
          top: 1000,
          left: 60,
          right: 60,
          display: 'flex',
          gap: 12,
        }}
      >
        {p.stages.map((stage, i) => {
          const start = stageStart + i * stageInterval;
          const progress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 14, stiffness: 140, mass: 0.5 },
          });

          return (
            <div
              key={i}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '18px 8px',
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.06)',
                opacity: progress,
                transform: `translateY(${(1 - progress) * 20}px)`,
              }}
            >
              <div style={{ fontSize: 34 }}>{stage.icon}</div>
              <div style={{ fontSize: 32, fontWeight: 700, marginTop: 8, color: primary }}>{stage.label}</div>
              <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.5)', marginTop: 6, lineHeight: 1.4 }}>
                {stage.description}
              </div>
            </div>
          );
        })}
      </div>

      {/* Real-life examples */}
      <div
        style={{
          position: 'absolute',
          top: 1240,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 38, color: 'rgba(255,255,255,0.35)', marginBottom: 16 }}>生活中的表现</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {p.examples.map((ex, i) => {
            const start = exampleStart + i * exampleInterval;
            const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
            const x = interpolate(frame, [start, start + 20], [40, 0], { extrapolateRight: 'clamp' });

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '18px 24px',
                  background: `rgba(255,255,255,0.03)`,
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.05)',
                  opacity,
                  transform: `translateX(${x}px)`,
                }}
              >
                <span style={{ fontSize: 32 }}>{ex.icon}</span>
                <span style={{ fontSize: 34, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                  {ex.scenario}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Takeaway */}
      <div
        style={{
          position: 'absolute',
          bottom: 150,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: takeawayOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '18px 36px',
            background: `linear-gradient(135deg, ${primary}15, ${secondary}15)`,
            border: `1px solid ${primary}35`,
            borderRadius: 18,
          }}
        >
          {/* <span style={{ fontSize: 34 }}>💡</span> */}
          <div style={{ fontSize: 34, fontWeight: 600, marginTop: 8, color: 'rgba(255,255,255,0.9)' }}>
          💡 {p.takeaway}
          </div>
        </div>
      </div>

      {/* Self reflection */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: reflectionOpacity,
        }}
      >
        <span style={{ fontSize: 34, color: 'rgba(255,255,255,0.4)' }}>🪞 {p.selfReflection}</span>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 660], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: `linear-gradient(90deg, ${primary}, ${secondary})`,
        }}
      />
    </AbsoluteFill>
  );
};
